import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (fileName: string) => void;
  files: File[];
}

const FileUpload = ({ onFilesSelected, onFileRemove, files }: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(file => file.name.toLowerCase().endsWith('.stl'));
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
      toast.success('Dateien erfolgreich hochgeladen');
    } else {
      toast.error('Bitte laden Sie nur STL-Dateien hoch');
    }
  }, [onFilesSelected]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-pki.stl': ['.stl'],
      'application/octet-stream': ['.stl'],
    },
    multiple: true,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-4">
          <Upload className="w-12 h-12 text-primary" />
          <div className="text-center">
            <p className="text-lg font-medium">STL-Dateien hier ablegen</p>
            <p className="text-sm text-muted-foreground">oder klicken zum Auswählen</p>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="relative border rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium truncate">{file.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onFileRemove(file.name)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;